/**
 * @fileoverview Input/output schemas for the plot-synopsis-extractor-chunked-v3 agent.
 *
 * This agent summarizes a single chunk of a larger document as part of a
 * map-reduce synopsis pipeline. Each chunk is processed independently,
 * and the per-chunk synopses are later merged into a coherent whole.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { ChunkInfoSchema } from '@/lib/content-chunking';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  /** The current chunk of text to summarize. May include overlap from adjacent chunks. */
  textChunk: z.string().describe('The current chunk of text to summarize.'),

  /** Metadata about this chunk position in the full document. */
  chunkInfo: ChunkInfoSchema.describe('Metadata about this chunk position in the full document.'),

  /** Target number of tokens for this chunk synopsis. */
  targetTokens: z.number().describe('Target number of tokens for this chunk synopsis (proportional to chunk size).'),

  /** Title of the book/story being summarized, if known. */
  title: z.string().optional().describe('Title of the book/story being summarized.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  /** Synopsis of this chunk of the story. */
  chunkSynopsis: z.string().describe('A detailed synopsis of this portion of the story, preserving all significant events, characters, and plot points.'),
});

export type Output = z.infer<typeof OutputSchema>;
